import { AppsFlyer } from '../plugins/apps_flyer_plugin.js';
import type { AppsFlyerAttributionRequest } from './dto/appsflyer_attribution.dto.js';
import { sendAppsFlyerAttribution } from './requests/appsflyer_attribution.requests.js';

const USER_SOURCE_SENT_PREFIX = 'user_source_sent_';
const USER_SOURCE_VALUE_KEY = 'user_source_value';
const ATTRIBUTION_TIMEOUT_MS = 10_000;
const ATTRIBUTION_POLL_INTERVAL_MS = 500;

function safeGet( key: string ): string | null
{
  try { return localStorage.getItem( key ); }
  catch { return null; }
}

function safeSet( key: string, value: string ): void
{
  try { localStorage.setItem( key, value ); }
  catch {}
}

function delay( ms: number ): Promise<void>
{
  return new Promise( resolve => setTimeout( resolve, ms ) );
}

function getStringField( data: Record<string, unknown>, keys: string[] ): string | null
{
  for ( const key of keys )
  {
    const value = data[key];
    if ( value == null ) continue;
    const text = String( value ).trim();
    if ( text.length > 0 ) return text;
  }
  return null;
}

function getBooleanField( data: Record<string, unknown>, keys: string[] ): boolean | null
{
  for ( const key of keys )
  {
    const value = data[key];
    if ( typeof value === 'boolean' ) return value;
    if ( typeof value === 'number' && Number.isFinite( value ) ) return value !== 0;
    if ( typeof value !== 'string' ) continue;
    const text = value.trim().toLowerCase();
    if ( text === 'true' || text === '1' || text === 'yes' ) return true;
    if ( text === 'false' || text === '0' || text === 'no' ) return false;
  }
  return null;
}

function sanitizeRawData( data: Record<string, unknown> | null ): Record<string, unknown>
{
  if ( !data ) return {};
  const raw: Record<string, unknown> = {};
  for ( const [key, value] of Object.entries( data ) )
  {
    raw[key] = value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? value
      : String( value );
  }
  return raw;
}

function cleanUserSource( source: string ): string
{
  const cleaned = source.trim().replace( /\s+/g, '_' );
  return cleaned.length > 0 ? cleaned.slice( 0, 128 ) : 'unknown';
}

function normalizeUserSource( data: Record<string, unknown> | null ): string
{
  if ( !data ) return 'unknown';
  const af_status = getStringField( data, ['af_status'] )?.toLowerCase() ?? '';
  const source = getStringField( data, ['media_source', 'pid', 'af_prt', 'af_channel', 'campaign', 'c'] );
  if ( af_status === 'organic' ) return 'organic';
  if ( source ) return cleanUserSource( source );
  if ( af_status === 'non-organic' || af_status === 'non_organic' ) return 'non_organic';
  return 'unknown';
}

async function waitForAppsFlyerConversionData(): Promise<Record<string, unknown> | null>
{
  if ( !AppsFlyer.isAvailable() ) return null;
  const started_at = Date.now();
  while ( Date.now() - started_at <= ATTRIBUTION_TIMEOUT_MS )
  {
    const data = await AppsFlyer.getConversionData();
    if ( data && Object.keys( data ).length > 0 ) return data;
    await delay( ATTRIBUTION_POLL_INTERVAL_MS );
  }
  return null;
}

export function getCachedUserSource(): string | null
{
  const source = safeGet( USER_SOURCE_VALUE_KEY );
  return source && source.length > 0 ? source : null;
}

export async function resolveInstallUserSource(): Promise<string>
{
  const cached = getCachedUserSource();
  if ( cached ) return cached;
  const data = await waitForAppsFlyerConversionData();
  const source = normalizeUserSource( data );
  safeSet( USER_SOURCE_VALUE_KEY, source );
  return source;
}

async function buildAppsFlyerAttributionPayload(): Promise<AppsFlyerAttributionRequest>
{
  const data = await waitForAppsFlyerConversionData();
  const raw_data = sanitizeRawData( data );
  const user_source = normalizeUserSource( raw_data );
  safeSet( USER_SOURCE_VALUE_KEY, user_source );

  return {
    user_source,
    appsflyer_id: await AppsFlyer.getAppsFlyerUID(),
    attribution_status: getStringField( raw_data, ['af_status'] ),
    media_source: getStringField( raw_data, ['media_source', 'pid'] ),
    campaign: getStringField( raw_data, ['campaign', 'c'] ),
    campaign_id: getStringField( raw_data, ['campaign_id', 'af_c_id'] ),
    channel: getStringField( raw_data, ['af_channel'] ),
    agency: getStringField( raw_data, ['agency', 'af_prt'] ),
    adset: getStringField( raw_data, ['adset'] ),
    adset_id: getStringField( raw_data, ['adset_id', 'af_adset_id'] ),
    adgroup: getStringField( raw_data, ['adgroup'] ),
    adgroup_id: getStringField( raw_data, ['adgroup_id'] ),
    ad_id: getStringField( raw_data, ['ad_id', 'af_ad_id'] ),
    install_time: getStringField( raw_data, ['install_time'] ),
    click_time: getStringField( raw_data, ['click_time'] ),
    http_referrer: getStringField( raw_data, ['http_referrer'] ),
    is_first_launch: getBooleanField( raw_data, ['is_first_launch'] ),
    is_retargeting: getBooleanField( raw_data, ['is_retargeting'] ),
    retargeting_conversion_type: getStringField( raw_data, ['retargeting_conversion_type'] ),
    af_click_lookback: getStringField( raw_data, ['af_click_lookback'] ),
    af_reengagement_window: getStringField( raw_data, ['af_reengagement_window'] ),
    af_siteid: getStringField( raw_data, ['af_siteid'] ),
    af_sub1: getStringField( raw_data, ['af_sub1'] ),
    af_sub2: getStringField( raw_data, ['af_sub2'] ),
    af_sub3: getStringField( raw_data, ['af_sub3'] ),
    af_sub4: getStringField( raw_data, ['af_sub4'] ),
    af_sub5: getStringField( raw_data, ['af_sub5'] ),
    shortlink: getStringField( raw_data, ['shortlink'] ),
    deep_link_value: getStringField( raw_data, ['deep_link_value'] ),
    af_dp: getStringField( raw_data, ['af_dp'] ),
    af_cost_value: getStringField( raw_data, ['af_cost_value'] ),
    cost_cents_usd: getStringField( raw_data, ['cost_cents_USD'] ),
    orig_cost: getStringField( raw_data, ['orig_cost'] ),
    af_cpi: getStringField( raw_data, ['af_cpi'] ),
    raw_data,
  };
}

export async function syncUserSourceAfterLogin( playerId: number ): Promise<void>
{
  const sent_key = `${USER_SOURCE_SENT_PREFIX}${playerId}`;
  if ( safeGet( sent_key ) === '1' ) return;

  const payload = await buildAppsFlyerAttributionPayload();

  try
  {
    await sendAppsFlyerAttribution( payload );
    safeSet( sent_key, '1' );
  }
  catch ( e )
  {
    console.warn( '[UserSource] Failed to sync user_source', e );
  }
}
